import { makeStyles } from '@material-ui/core';
import Tippy from '@tippyjs/react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import Button from '~components/button';
import { BasicClose } from '~svg-components';

const useStyles = makeStyles((theme) => ({
  root: {
    '&.tippy-box': {
      backgroundColor: theme.colors.white,
      borderRadius: theme.borderRadius.small,
      position: 'relative',
      boxShadow: theme.shadow.componentDark,

      '&& > .tippy-arrow': {
        color: theme.colors.white,
      },

      '& > .tippy-content': {
        padding: 0,
      },
    },
  },

  closeButton: {
    position: 'absolute',
    width: 32,
    height: 32,
    top: 8,
    right: 8,
    zIndex: 1,
  },
}));

const defaultModifiers = [
  {
    name: 'flip',
    options: {
      fallbackPlacements: ['top', 'bottom', 'right', 'left'],
    },
  },
  {
    name: 'preventOverflow',
    options: {
      altAxis: true,
      padding: 8,
    },
  },
];

const TippyPopper = React.forwardRef((props, ref) => {
  const {
    withCloseButton,
    lazy = true,
    content,
    onMount,
    onShow,
    onHidden,
    className,
    popperOptions,
    ...otherProps
  } = props;

  const [instance, setInstance] = useState(null);
  const [mounted, setMounted] = useState(false);

  const handleHide = (...args) => {
    instance.hide(...args);
  };

  const handleMount = (arg) => {
    if (instance !== arg) {
      setInstance(arg);
    }
    if (onMount) onMount(arg);
  };

  const handleShow = (arg) => {
    setMounted(true);
    if (onShow) onShow(arg);
  };

  const handleHidden = (arg) => {
    setMounted(false);
    if (onHidden) onHidden(arg);
  };

  const classes = useStyles();

  const renderContent = () => (
    <>
      {withCloseButton && (
        <Button variant="text" onClick={handleHide} className={classes.closeButton}>
          <BasicClose fontSize={16} color="grey66" />
        </Button>
      )}
      {content}
    </>
  );

  return (
    <Tippy
      interactive
      trigger="click"
      offset={[0, 4]}
      arrow={!!otherProps?.arrow}
      {...otherProps}
      ref={ref}
      content={!lazy || mounted ? renderContent() : ''}
      className={classNames(classes.root, className)}
      onMount={handleMount}
      onShow={handleShow}
      onHidden={handleHidden}
      popperOptions={{
        ...popperOptions,
        modifiers: [...defaultModifiers, ...(popperOptions?.modifiers ?? [])],
      }}
    />
  );
});

TippyPopper.propTypes = {
  content: PropTypes.any,
  withCloseButton: PropTypes.bool,
  lazy: PropTypes.bool,
  className: PropTypes.string,
  popperOptions: PropTypes.object,
  onMount: PropTypes.func,
  onShow: PropTypes.func,
  onHidden: PropTypes.func,
};

export default TippyPopper;
